import dayjs from 'dayjs';

import { AGGREGATION_TYPE, AGGREGATION_TYPE_LABELS, VALUE_DIFFERENCE_DISPLAY } from 'components/View/constants';
import { CommunityOccupancy, UnitSystem } from 'types/communities';
import { calculateCommunityDistance, calculateOccupancy, groupAndSortCommunityContracts } from 'utils/data/communities';
import {
  convertAreaByUnitSystem,
  convertRatePerAreaByUnitSystem,
  getAreaUnitLabel,
  getDistanceUnitLabel,
  getRatePerAreaUnitLabel,
  resolveUnitSystem,
} from 'utils/helpers/units';

import {
  CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW,
  CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW_ROOM_COMPARISON,
  CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW,
  CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW_ROOM_COMPARISON,
  CMA_SUMMARY_CARE_FEE_DATA_ROW,
  CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW,
  CMA_SUMMARY_COMMUNITY_CONTRACT_ROW,
  CMA_SUMMARY_CONTRACT_BENEFIT_DATA_ROW,
  CMA_SUMMARY_DATA_ROW_TYPE,
  CMA_SUMMARY_FEE_LABELS,
  CMA_SUMMARY_LIVING_TYPE_DATA_ROW,
  CMA_SUMMARY_LIVING_TYPE_FEE_DATA_ROW,
  CMA_SUMMARY_MEDICATION_FEE_DATA_ROW,
  CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW,
  CMA_SUMMARY_NOT_APPLICABLE_VALUE,
  CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS,
} from './constants';
import { CMADataCellValue, CMASummaryFilters } from './types';
import {
  formatAmount,
  formatPercentage,
  getCMASummaryReviewSources,
  parseCMASummaryCommunityData,
  parseCMASummaryFeeData,
} from './utils';
import { COMMUNITY_CONTRACT_TYPE } from '../../constants';

const FIXED_TITLE_COLUMNS = ['Type', 'Field'];
const COLUMN_TYPE = {
  GENERAL_INFO: 'General Info',
  OTHER: 'Other',
};

const formatNumber = (num: number | undefined) => {
  if (!num || typeof num !== 'number') {
    return num;
  }

  // Check if there are non-zero decimals after rounding to two decimal places
  return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
};

const formatDecimalNumber = (num: any, decimals: number) => {
  const numericValue = typeof num === 'number' ? num : Number(num);
  return Number.isFinite(numericValue) ? numericValue.toFixed(decimals) : num;
};

const parseFiniteNumber = (value: any) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const numericValue = Number(value.replace(/[$,"]/g, '').trim());
  return Number.isFinite(numericValue) ? numericValue : null;
};

const convertRatePerAreaValue = (value: any, unitSystem: UnitSystem) => {
  const numericValue = parseFiniteNumber(value);
  return numericValue === null ? value : convertRatePerAreaByUnitSystem(numericValue, unitSystem);
};

const convertAreaValue = (value: any, unitSystem: UnitSystem) => {
  const numericValue = parseFiniteNumber(value);
  return numericValue === null ? value : convertAreaByUnitSystem(numericValue, unitSystem);
};

const formatFixedDecimalCSVCell = (value: any, decimals: number, trimZeroDecimals = false) => {
  if (value === null || value === undefined || value === '' || value === CMA_SUMMARY_NOT_APPLICABLE_VALUE) {
    return value;
  }

  const stringValue = String(value).replace(/^"|"$/g, '');
  const match = stringValue.match(/^(\$?)(-?\d[\d,]*(?:\.\d+)?)(.*)$/);
  if (!match) {
    return value;
  }

  const [, prefix, rawNumber, suffix] = match;
  const numericValue = parseFiniteNumber(rawNumber);
  if (numericValue === null) {
    return value;
  }

  const formattedNumber = numericValue.toFixed(decimals);
  return `"${prefix}${trimZeroDecimals ? formattedNumber.replace(/\.00$/, '') : formattedNumber}${suffix}"`;
};

const getFormattedDifference = (difference: number | null | undefined, filters: Partial<CMASummaryFilters>) => {
  if (filters?.viewOptions?.valueDisplay === VALUE_DIFFERENCE_DISPLAY.PERCENTAGES) {
    return formatPercentage(difference);
  } else if (filters?.viewOptions?.valueDisplay === VALUE_DIFFERENCE_DISPLAY.AMOUNTS) {
    return formatAmount('$', difference);
  }
};

const csvSafeValue = (value: any) => {
  if (value == null) return '';
  const stringValue = String(value);
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

export const getCMASummaryRatingRowsCSV = (rowContent: any, filters: Partial<CMASummaryFilters>) => {
  const ratings: (string | number | null | undefined)[] = [];
  const reviews: (string | number | null | undefined)[] = [];

  rowContent.forEach((item: (string | number)[]) => {
    const rating = item[0];
    const reviewsNumber = item[1];

    // ratings
    const parsedRating = typeof rating === 'string' ? parseFloat(rating) : rating;
    const parsedRatingData = parseCMASummaryCommunityData(parsedRating, filters?.viewOptions?.valueDisplay);
    const ratingValue = parsedRatingData.dataItemOne?.value;
    let ratingDifference: string | number | undefined | null = parsedRatingData.dataItemOne?.difference;
    if (filters?.viewOptions?.valueDisplay === VALUE_DIFFERENCE_DISPLAY.PERCENTAGES) {
      ratingDifference = formatPercentage(ratingDifference);
    } else if (filters?.viewOptions?.valueDisplay === VALUE_DIFFERENCE_DISPLAY.AMOUNTS) {
      ratingDifference = typeof ratingDifference === 'number' ? ratingDifference.toFixed(1) : ratingDifference;
    }
    ratings.push(ratingDifference ? `${ratingValue} (difference to community: ${ratingDifference})` : ratingValue);

    // reviews
    const parsedReviewsNumber: number = typeof reviewsNumber === 'string' ? parseFloat(reviewsNumber) : reviewsNumber;
    const parsedReviewsData = parseCMASummaryCommunityData(parsedReviewsNumber, filters?.viewOptions?.valueDisplay);
    const reviewsNumberValue = parsedReviewsData.dataItemOne?.value;
    let reviewsNumberDifference: string | number | undefined | null = parsedReviewsData.dataItemOne?.difference;
    if (filters?.viewOptions?.valueDisplay === VALUE_DIFFERENCE_DISPLAY.PERCENTAGES) {
      reviewsNumberDifference = formatPercentage(reviewsNumberDifference);
    }
    reviews.push(
      reviewsNumberDifference
        ? `${reviewsNumberValue} (difference to community: ${reviewsNumberDifference})`
        : reviewsNumberValue
    );
  });

  return { ratings, reviews };
};

const getCMASummaryDataRowsCSV = (rowContent: any, filters: Partial<CMASummaryFilters>, unitSystem: UnitSystem) => {
  const rates: (string | undefined | null)[] = [];
  const squareFootages: (string | undefined | null)[] = [];
  const entranceFees: (string | undefined | null)[] = [];
  const pricePerSqFeet: (string | undefined | null)[] = [];

  rowContent.forEach((item: CMADataCellValue | CMADataCellValue[]) => {
    const parsedData = parseCMASummaryCommunityData(item, filters?.viewOptions?.valueDisplay);
    const rent = parsedData.dataItemOne?.value;
    const rentDifference = parsedData.dataItemOne?.difference;
    const entranceFee = parsedData.dataItemTwo?.value;
    const entranceFeeDifference = parsedData.dataItemTwo?.difference;
    const squareFootageRaw = parsedData.dataItemThree?.value;
    const squareFootageDifferenceRaw = parsedData.dataItemThree?.difference;
    const pricePerSquareFootRaw = parsedData.dataItemFour?.value;
    const pricePerSquareFootDifferenceRaw = parsedData.dataItemFour?.difference;
    const squareFootage = convertAreaValue(squareFootageRaw, unitSystem);
    const squareFootageDifference = convertAreaValue(squareFootageDifferenceRaw, unitSystem);
    const pricePerSquareFoot = convertRatePerAreaValue(pricePerSquareFootRaw, unitSystem);
    const pricePerSquareFootDifference = convertRatePerAreaValue(pricePerSquareFootDifferenceRaw, unitSystem);

    let formattedRentDiff, formattedSqFtDiff, formattedPricePerSqFtDiff, formattedEntFeeDiff;
    if (filters?.viewOptions?.valueDisplay === VALUE_DIFFERENCE_DISPLAY.PERCENTAGES) {
      formattedRentDiff = formatPercentage(rentDifference);
      formattedEntFeeDiff = formatPercentage(entranceFeeDifference);
      formattedSqFtDiff = formatPercentage(squareFootageDifference);
      formattedPricePerSqFtDiff = formatPercentage(pricePerSquareFootDifference);
    } else if (filters?.viewOptions?.valueDisplay === VALUE_DIFFERENCE_DISPLAY.AMOUNTS) {
      formattedRentDiff = formatAmount('$', rentDifference);
      formattedEntFeeDiff = formatAmount('$', entranceFeeDifference);
      formattedSqFtDiff = formatAmount('', squareFootageDifference);
      formattedPricePerSqFtDiff = formatAmount('$', pricePerSquareFootDifference);
    }

    if (rent !== undefined && rent !== null && squareFootage !== undefined) {
      rates.push(formattedRentDiff ? `"$${rent} (difference to community: ${formattedRentDiff})"` : `"$${rent}"`);
    } else {
      rates.push('');
    }
    if (entranceFee !== undefined && entranceFee !== null && squareFootage !== undefined) {
      entranceFees.push(
        formattedEntFeeDiff
          ? `"$${entranceFee} (difference to community: ${formattedEntFeeDiff})"`
          : `"$${entranceFee}"`
      );
    } else {
      entranceFees.push('');
    }
    if (squareFootage !== null && squareFootage !== undefined) {
      squareFootages.push(
        formattedSqFtDiff ? `"${squareFootage} (difference to community: ${formattedSqFtDiff})"` : `"${squareFootage}"`
      );
    } else {
      squareFootages.push('');
    }

    if (pricePerSquareFoot !== null && pricePerSquareFoot !== undefined) {
      const formattedPricePerSquareFoot = formatDecimalNumber(pricePerSquareFoot, 2);
      pricePerSqFeet.push(
        formattedPricePerSqFtDiff
          ? `"$${formattedPricePerSquareFoot} (difference to community: ${formattedPricePerSqFtDiff})"`
          : `"$${formattedPricePerSquareFoot}"`
      );
    } else {
      pricePerSqFeet.push('');
    }
  });

  return { rates, entranceFees, squareFootages, pricePerSqFeet };
};

const getCMASummaryLivingTypeFeeRowsCSV = (rowContent: any, filters: Partial<CMASummaryFilters>) =>
  rowContent.map((item: (number | number[])[] | string) => {
    if (item === CMA_SUMMARY_NOT_APPLICABLE_VALUE) {
      return CMA_SUMMARY_NOT_APPLICABLE_VALUE;
    }

    if (!Array.isArray(item)) {
      return '';
    }
    const [fee] = item;
    const [feeValue, feeDifference] = parseCMASummaryFeeData(fee, filters?.viewOptions?.valueDisplay);

    const formattedFeeDifferenceValue = getFormattedDifference(feeDifference, filters);
    if (feeValue) {
      return formattedFeeDifferenceValue
        ? `"$${feeValue} (difference to community: ${formattedFeeDifferenceValue})"`
        : `"$${feeValue}"`;
    } else {
      return '';
    }
  });

const getCMASummaryFeeRowCSV = (rowContent: any, filters: Partial<CMASummaryFilters>) =>
  rowContent.map((item: number | number[] | string) => {
    if (item === CMA_SUMMARY_NOT_APPLICABLE_VALUE) {
      return CMA_SUMMARY_NOT_APPLICABLE_VALUE;
    }

    const [feeValue, feeDifference] = parseCMASummaryFeeData(item, filters?.viewOptions?.valueDisplay);

    const formattedFeeDifferenceValue = getFormattedDifference(feeDifference, filters);
    if (feeValue) {
      if (Array.isArray(feeValue) && typeof feeValue[0] === 'number') {
        feeValue[0] = Math.round(feeValue[0]);
      }
      if (formattedFeeDifferenceValue) {
        return `"$${formatNumber(feeValue)} (difference to community: ${formattedFeeDifferenceValue})"`;
      } else {
        return Array.isArray(feeValue)
          ? feeValue.length > 0 && typeof feeValue[0] === 'number'
            ? `"$${formatNumber(feeValue[0])}, ${feeValue.slice(1).join(', ')}"`
            : `"${feeValue.filter((item: any) => item != null && item !== '').join(', ')}"`
          : typeof feeValue === 'number'
            ? `"$${formatNumber(feeValue)}"`
            : `${feeValue ?? ''}`;
      }
    } else {
      return '';
    }
  });

const getCMASummaryComparisonRowCSV = (rowContent: any, filters: Partial<CMASummaryFilters>, prefix = '') =>
  rowContent.map((item: number | number[]) => {
    const parsedComparisonRow = parseCMASummaryCommunityData(item, filters?.viewOptions?.valueDisplay);
    const comparisonRowValue = parsedComparisonRow.dataItemOne?.value;
    const comparisonRowDifference = parsedComparisonRow.dataItemOne?.difference;

    const formattedDifference = getFormattedDifference(comparisonRowDifference, filters);
    if (comparisonRowValue !== undefined && comparisonRowValue !== null) {
      return formattedDifference
        ? `"${prefix}${comparisonRowValue} (difference to community: ${formattedDifference})"`
        : `"${prefix}${comparisonRowValue}"`;
    } else {
      return '';
    }
  });

const getRoomComparisonRowTitleCSV = (rowMetadata: any) => {
  const titleParts = Array.isArray(rowMetadata.title) ? rowMetadata.title : [rowMetadata.title].filter(Boolean);
  const subtitleParts = Array.isArray(rowMetadata.subtitle) ? rowMetadata.subtitle : [];
  const rowTitle = rowMetadata.apartmentGroupType || rowMetadata.apartmentType;

  const comparisonTitle = titleParts
    .map((item: string, index: number) => {
      const shouldShowAggregation =
        item === CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS.RATE ||
        item === CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS.CARE;
      const subtitle = shouldShowAggregation ? subtitleParts[index] : '';
      return subtitle ? `${item} (${subtitle})` : item;
    })
    .join(' + ');

  return [rowTitle, comparisonTitle].filter(Boolean).join(' ');
};

const getFeeNotes = (rows: any[]) =>
  rows.map((item: any[]) => {
    const n = item?.[3];
    return n == null || n === '' ? '""' : `"${String(n).trim()}"`;
  });

const stripNoteFromValue = (value: string, note: string) => {
  let inner = value.replace(/^"|"$/g, '').trim();
  if (note) {
    const suffixWithComma = `, ${note}`;
    if (inner.endsWith(suffixWithComma)) {
      inner = inner.slice(0, -suffixWithComma.length).trim();
    } else if (inner.endsWith(note)) {
      inner = inner
        .slice(0, -note.length)
        .replace(/[, ]+$/, '')
        .trim();
    }
  }
  return inner ? `"${inner}"` : '""';
};

const isMedicationFeeRowType = (rowType: string) =>
  rowType === CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW || rowType === CMA_SUMMARY_MEDICATION_FEE_DATA_ROW;

const getFeeLabelByRowType = (rowType: string) =>
  isMedicationFeeRowType(rowType) ? CMA_SUMMARY_FEE_LABELS.MEDICATION : CMA_SUMMARY_FEE_LABELS.CARE;

const getFeeAggregationLabel = (rowType: string, filters: CMASummaryFilters) => {
  const aggregationValue = isMedicationFeeRowType(rowType)
    ? filters?.medicationLevelsStatsType
    : filters?.careLevelsStatsType;
  return AGGREGATION_TYPE_LABELS[aggregationValue || AGGREGATION_TYPE.AVG];
};

const getFeeValues = (fees: string[], rows: any[]) =>
  fees.map((feeStr, i) => {
    if (feeStr == null) return '""';
    const s = String(feeStr).trim();
    if (s === '' || s === '""') return '""';
    const note = rows?.[i]?.[3] ? String(rows[i][3]).trim() : '';
    return stripNoteFromValue(s, note);
  });

const isRatePerAreaCSVRow = (row: any[], ratePerAreaUnitLabel: string) => {
  const field = String(row?.[1] ?? '')
    .replace(/^"|"$/g, '')
    .toLowerCase();
  return field.includes(`rate ${ratePerAreaUnitLabel.toLowerCase()}`);
};

const isAreaCSVRow = (row: any[], areaUnitLabel: string) => {
  const field = String(row?.[1] ?? '')
    .replace(/^"|"$/g, '')
    .toLowerCase();
  return field.includes(areaUnitLabel.toLowerCase()) && !field.includes('rate ');
};

export const transformCSV = (community: any, data: any, filters?: CMASummaryFilters) => {
  if (!filters || !data) {
    return;
  }
  const unitSystem = resolveUnitSystem(community);
  const distanceUnit = getDistanceUnitLabel(unitSystem);
  const areaUnitLabel = getAreaUnitLabel(unitSystem);
  const ratePerAreaUnitLabel = getRatePerAreaUnitLabel(unitSystem);

  const csvData: any[] = [];
  const roomTypeAggregation = AGGREGATION_TYPE_LABELS[filters?.roomTypesPriceDisplay || AGGREGATION_TYPE.AVG];

  (data || []).forEach((row: any) => {
    const rowMetadata = row[0];
    const rowContent = row.slice(1);

    switch (rowMetadata?.type) {
      // Form the header CSV row
      // Fixed columns Type and Field, variable columns of Community and it's Competitors
      case CMA_SUMMARY_DATA_ROW_TYPE.COMMUNITY_NAME:
        csvData.push([...FIXED_TITLE_COLUMNS, ...rowContent.map((item: string) => (item ? `"${item}"` : undefined))]);
        break;
      case CMA_SUMMARY_DATA_ROW_TYPE.ADDRESS:
        csvData.push([
          COLUMN_TYPE.GENERAL_INFO,
          'Address',
          ...rowContent.map((item: string) => (item ? `"${item}"` : undefined)),
        ]);
        break;
      case CMA_SUMMARY_DATA_ROW_TYPE.DISTANCE:
        csvData.push([
          COLUMN_TYPE.GENERAL_INFO,
          'Distance',
          ...rowContent.map((item: any) =>
            item === CMA_SUMMARY_NOT_APPLICABLE_VALUE
              ? CMA_SUMMARY_NOT_APPLICABLE_VALUE
              : item !== null && item !== undefined
                ? `${calculateCommunityDistance(item, unitSystem)} ${distanceUnit}`
                : '-'
          ),
        ]);
        break;
      case CMA_SUMMARY_DATA_ROW_TYPE.WEBSITE:
        csvData.push([COLUMN_TYPE.GENERAL_INFO, 'Website', ...rowContent]);
        break;
      case CMA_SUMMARY_DATA_ROW_TYPE.PHONE:
        csvData.push([COLUMN_TYPE.GENERAL_INFO, 'Phone', ...rowContent]);
        break;
      case CMA_SUMMARY_DATA_ROW_TYPE.YEAR_OPENED:
        csvData.push([COLUMN_TYPE.GENERAL_INFO, 'Year Opened', ...rowContent]);
        break;
      case CMA_SUMMARY_DATA_ROW_TYPE.NOTES:
        csvData.push([COLUMN_TYPE.GENERAL_INFO, 'Community Notes', ...rowContent.map(csvSafeValue)]);
        break;
      case CMA_SUMMARY_DATA_ROW_TYPE.SHOP_DATE:
        csvData.push([
          COLUMN_TYPE.GENERAL_INFO,
          'Shop Date',
          ...rowContent.map((item: string) =>
            item === CMA_SUMMARY_NOT_APPLICABLE_VALUE
              ? CMA_SUMMARY_NOT_APPLICABLE_VALUE
              : item
                ? `"${dayjs(item).format('MMM DD YYYY')}"`
                : undefined
          ),
        ]);
        break;
      // Concatenate all address parts
      case CMA_SUMMARY_DATA_ROW_TYPE.REVIEWS: {
        const subtitles = rowMetadata.subtitle ?? [];
        const allSourcesTitle = subtitles[0] || 'All Sources';
        const childSources = subtitles.slice(1);
        const { ratings, reviews } = getCMASummaryRatingRowsCSV(rowContent, filters);

        csvData.push([COLUMN_TYPE.GENERAL_INFO, `"Rating (${allSourcesTitle})"`, ...ratings]);
        csvData.push([COLUMN_TYPE.GENERAL_INFO, `"Number of reviews (${allSourcesTitle})"`, ...reviews]);

        childSources.forEach((sourceName: any, i: number) => {
          const sourceRatings: (string | number)[] = [];
          const sourceReviews: (string | number)[] = [];

          rowContent.forEach((row: (string | number)[]) => {
            const pair = row[i + 2];
            if (typeof pair === 'string' && pair.includes('·')) {
              const [rating, total] = pair.split('·').map((v) => v.trim());
              const [reviews, source] = total.split(',').map((v) => v.trim());
              sourceRatings.push(rating);
              sourceReviews.push(reviews);
            } else {
              sourceRatings.push('');
              sourceReviews.push('');
            }
          });

          csvData.push([COLUMN_TYPE.GENERAL_INFO, `"Rating (${sourceName})"`, ...sourceRatings]);
          csvData.push([COLUMN_TYPE.GENERAL_INFO, `"Number of reviews (${sourceName})"`, ...sourceReviews]);
        });

        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.CARE_TYPES: {
        csvData.push([
          COLUMN_TYPE.GENERAL_INFO,
          'Care types',
          ...rowContent.map((item: any) => {
            if (item === CMA_SUMMARY_NOT_APPLICABLE_VALUE) {
              return CMA_SUMMARY_NOT_APPLICABLE_VALUE;
            }
            if (Array.isArray(item) && item.length > 0) {
              return item.filter(Boolean).join(' | ');
            }
            return '';
          }),
        ]);
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.CONTRACT_TYPE:
        csvData.push([
          COLUMN_TYPE.GENERAL_INFO,
          'Contract type',
          ...rowContent.map((item: any) => {
            if (item === CMA_SUMMARY_NOT_APPLICABLE_VALUE) {
              return CMA_SUMMARY_NOT_APPLICABLE_VALUE;
            }
            if (!Array.isArray(item) || item.length === 0) {
              return '';
            }

            const sortedGroups = groupAndSortCommunityContracts(item);
            const formatPercentages = (values: number[]) => values.map((v) => `${v}%`).join(', ');

            const results = sortedGroups
              .map((group) =>
                group.type === COMMUNITY_CONTRACT_TYPE.RENTAL
                  ? group.name
                  : `${group.name} (${formatPercentages(group.percentages)})`
              )
              .join(' | ');
            return `"${results}"`;
          }),
        ]);
        break;
      case CMA_SUMMARY_DATA_ROW_TYPE.OCCUPANCY:
        csvData.push([
          COLUMN_TYPE.GENERAL_INFO,
          'Occupancy overall',
          ...rowContent.map((occupancies: CommunityOccupancy[] | number | string) => {
            if (occupancies === CMA_SUMMARY_NOT_APPLICABLE_VALUE) {
              return CMA_SUMMARY_NOT_APPLICABLE_VALUE;
            }
            if (typeof occupancies === 'number') {
              return `${occupancies.toFixed(0)}%`;
            }
            if (!Array.isArray(occupancies)) {
              return '';
            }
            const { totalUnits, occupiedUnits, occupancyPercentage } = calculateOccupancy(occupancies);
            const unitsRatio = totalUnits ? `(${occupiedUnits}/${totalUnits})` : '';
            return occupancies.length > 0 ? `${occupancyPercentage}% ${unitsRatio}`.trim() : '';
          }),
        ]);
        break;
      // Check for presence of occupancies data for Living Type
      case CMA_SUMMARY_LIVING_TYPE_DATA_ROW:
        if (rowMetadata.hasOccupancies) {
          csvData.push([
            rowMetadata.title,
            'Occupancy',
            ...rowContent.map((item: [number, number, number]) => (item[0] ? `${item[0]}%` : undefined)),
          ]);
          csvData.push([
            rowMetadata.title,
            'Total number of units',
            ...rowContent.map((item: [number, number, number]) => item[1]),
          ]);
          csvData.push([
            rowMetadata.title,
            'Occupied number of units',
            ...rowContent.map((item: [number, number, number]) => (item[1] && item[2] ? item[1] - item[2] : undefined)),
          ]);
        }
        break;
      case CMA_SUMMARY_CONTRACT_BENEFIT_DATA_ROW: {
        // Step 1: Flatten benefits per community (keeping category grouping)
        const flattenedPerCommunity: string[][] = rowContent.map((grouped: any) => {
          if (grouped === CMA_SUMMARY_NOT_APPLICABLE_VALUE) return [CMA_SUMMARY_NOT_APPLICABLE_VALUE];
          if (!grouped) return [];

          return Object.entries(grouped as Record<string, string[]>).flatMap(([category, benefits]) =>
            benefits.map((b: string) => `${category}: ${b}`)
          );
        });

        // Step 2: Determine the maximum number of benefit rows
        const numberOfBenefitRows = flattenedPerCommunity.reduce((max: number, current: string[]) => {
          return Math.max(max, current.length);
        }, 0);

        // Step 3: Write CSV rows
        for (let i = 0; i < numberOfBenefitRows; i++) {
          csvData.push([
            'Contract Benefits',
            rowMetadata.title,
            ...flattenedPerCommunity.map(
              (benefits: string[]) =>
                `"${benefits[0] === CMA_SUMMARY_NOT_APPLICABLE_VALUE ? CMA_SUMMARY_NOT_APPLICABLE_VALUE : benefits[i] || ''}"`
            ),
          ]);
        }
        break;
      }
      case CMA_SUMMARY_COMMUNITY_CONTRACT_ROW: {
        csvData.push([
          rowMetadata.subtitle,
          `Contract Type`,
          ...rowContent.map((items: (number | string)[] | string) =>
            items === CMA_SUMMARY_NOT_APPLICABLE_VALUE ? CMA_SUMMARY_NOT_APPLICABLE_VALUE : items[0]
          ),
        ]);
        csvData.push([
          rowMetadata.subtitle,
          `Contract Refund Percentage`,
          ...rowContent.map((items: (number | string)[] | string) =>
            items === CMA_SUMMARY_NOT_APPLICABLE_VALUE
              ? CMA_SUMMARY_NOT_APPLICABLE_VALUE
              : items[1]
                ? `${items[1]}%`
                : ''
          ),
        ]);
        break;
      }
      case CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW: {
        const { rates, entranceFees, squareFootages, pricePerSqFeet } = getCMASummaryDataRowsCSV(
          rowContent,
          filters,
          unitSystem
        );
        csvData.push([rowMetadata.subtitle, `"${rowMetadata.title} Rate (${roomTypeAggregation})"`, ...rates]);
        if (entranceFees.some((entFee) => entFee?.length)) {
          csvData.push([
            rowMetadata.subtitle,
            `"${rowMetadata.title} Entrance Fee (${roomTypeAggregation})"`,
            ...entranceFees,
          ]);
        }
        if (squareFootages.some((sqFts) => sqFts?.length)) {
          csvData.push([
            rowMetadata.subtitle,
            `"${rowMetadata.title} ${areaUnitLabel} (${roomTypeAggregation})"`,
            ...squareFootages,
          ]);
        }
        if (pricePerSqFeet.some((price) => price?.length)) {
          csvData.push([
            rowMetadata.subtitle,
            `"${rowMetadata.title} Rate ${ratePerAreaUnitLabel} (${roomTypeAggregation})"`,
            ...pricePerSqFeet,
          ]);
        }
        break;
      }
      case CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW: {
        const { rates, entranceFees, squareFootages, pricePerSqFeet } = getCMASummaryDataRowsCSV(
          rowContent,
          filters,
          unitSystem
        );
        csvData.push([
          rowMetadata.subtitle,
          rowMetadata.titleMeta ? `${rowMetadata.title} (${rowMetadata.titleMeta}) Rate` : `${rowMetadata.title} Rate`,
          ...rates,
        ]);
        if (entranceFees.some((entFee) => entFee?.length)) {
          csvData.push([
            rowMetadata.subtitle,
            rowMetadata.titleMeta
              ? `${rowMetadata.title} (${rowMetadata.titleMeta})  Entrance Fee`
              : `${rowMetadata.title} Entrance Fee`,
            ...entranceFees,
          ]);
        }
        if (squareFootages.some((sqFts) => sqFts?.length)) {
          csvData.push([
            rowMetadata.subtitle,
            rowMetadata.titleMeta
              ? `${rowMetadata.title} (${rowMetadata.titleMeta}) ${areaUnitLabel}`
              : `${rowMetadata.title} ${areaUnitLabel}`,
            ...squareFootages,
          ]);
        }
        if (pricePerSqFeet.some((price) => price?.length)) {
          csvData.push([
            rowMetadata.subtitle,
            rowMetadata.titleMeta
              ? `${rowMetadata.title} (${rowMetadata.titleMeta}) Rate ${ratePerAreaUnitLabel}`
              : `${rowMetadata.title} Rate ${ratePerAreaUnitLabel}`,
            ...pricePerSqFeet,
          ]);
        }
        break;
      }
      case CMA_SUMMARY_LIVING_TYPE_FEE_DATA_ROW: {
        const fees = getCMASummaryLivingTypeFeeRowsCSV(rowContent, filters);
        csvData.push([rowMetadata.subtitle, rowMetadata.title, ...fees]);
        break;
      }
      case CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW:
      case CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW: {
        const feeLabel = getFeeLabelByRowType(rowMetadata.type);
        csvData.push([
          rowMetadata.living_type,
          `${feeLabel} Type`,
          ...rowContent.map((item: any[] | string) => {
            if (item === CMA_SUMMARY_NOT_APPLICABLE_VALUE) {
              return CMA_SUMMARY_NOT_APPLICABLE_VALUE;
            }

            return Array.isArray(item) ? item[1] ?? '' : '';
          }),
        ]);
        const averageFees: string[] = getCMASummaryFeeRowCSV(rowContent, filters);
        const feeNotes = getFeeNotes(rowContent);
        const feeValues = getFeeValues(averageFees, rowContent);

        const aggregationLabel = getFeeAggregationLabel(rowMetadata.type, filters);
        csvData.push([rowMetadata.living_type, `${feeLabel} (${aggregationLabel})`, ...feeValues]);

        if (feeNotes.some((n) => n && n !== '""')) {
          csvData.push([rowMetadata.living_type, `${feeLabel} Notes`, ...feeNotes]);
        }
        break;
      }
      case CMA_SUMMARY_CARE_FEE_DATA_ROW:
      case CMA_SUMMARY_MEDICATION_FEE_DATA_ROW: {
        const feeLabel = getFeeLabelByRowType(rowMetadata.type);
        const fees: string[] = getCMASummaryFeeRowCSV(rowContent, filters);
        csvData.push([rowMetadata.living_type, `${feeLabel} ${rowMetadata.title}`, ...fees]);
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.INCENTIVES: {
        const rowIncentivesWithContent = rowContent.map((incentives: any[]) => [
          ...(Array.isArray(incentives)
            ? incentives.filter((incentive: any) => Boolean(incentive.description))
            : incentives === CMA_SUMMARY_NOT_APPLICABLE_VALUE
              ? [{ description: CMA_SUMMARY_NOT_APPLICABLE_VALUE }]
              : []),
        ]);
        const incentivesMaxCount = Math.max(...rowIncentivesWithContent.map((incentives: any[]) => incentives.length));

        for (let i = 0; i < incentivesMaxCount; i++) {
          csvData.push([
            COLUMN_TYPE.OTHER,
            'Incentive',
            ...rowIncentivesWithContent.map((incentives: any[]) =>
              incentives[i] ? csvSafeValue(incentives[i].description) : ''
            ),
          ]);
          csvData.push([
            COLUMN_TYPE.OTHER,
            '',
            ...rowIncentivesWithContent.map((incentives: any[]) =>
              incentives[i]?.created_at ? `"${dayjs(incentives[i].created_at).format('MMM DD YYYY')}"` : undefined
            ),
          ]);
        }
        break;
      }
      case CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW_ROOM_COMPARISON: {
        const comparisonRowContent = getCMASummaryComparisonRowCSV(rowContent, filters, '$');
        csvData.push([rowMetadata.livingType, getRoomComparisonRowTitleCSV(rowMetadata), ...comparisonRowContent]);
        break;
      }
      case CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW_ROOM_COMPARISON: {
        const comparisonRowContent = getCMASummaryComparisonRowCSV(rowContent, filters, '$');
        csvData.push([rowMetadata.livingType, getRoomComparisonRowTitleCSV(rowMetadata), ...comparisonRowContent]);
        break;
      }
    }
  });

  // Merge available data together
  let csvContent = '';
  csvData.forEach((row: any) => {
    let normalizedRow = row;
    if (isRatePerAreaCSVRow(row, ratePerAreaUnitLabel)) {
      normalizedRow = row.map((cell: any, index: number) =>
        index > 1 ? formatFixedDecimalCSVCell(cell, 2, true) : cell
      );
    } else if (isAreaCSVRow(row, areaUnitLabel)) {
      normalizedRow = row.map((cell: any, index: number) => (index > 1 ? formatFixedDecimalCSVCell(cell, 0) : cell));
    }
    csvContent += normalizedRow.join(',') + '\n';
  });

  // Generate binary Blob and initiate download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8,' });
  const objUrl = URL.createObjectURL(blob);
  const currentDate = new Date().toLocaleDateString();
  const communityName = community?.comunity_name.split(' ').join('_');
  const filename = `CMA_Summary_${communityName}_${currentDate}.csv`;

  const link = document.createElement('a');
  link.setAttribute('href', objUrl);
  link.setAttribute('download', filename);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
